"use client"

import {useEffect} from "react";
import {getOrchestrator} from "@/lib/dialog-orchestrator/singleton";
import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {WalletTabsEnum} from "@/lib/enums/WalletTabsEnum";
import {installCloseIntentTracker, resolveCloseType} from "@/lib/analytics/close-intent";
import {PopupName, resolvePopupName} from "@/lib/analytics/dialog-popup-map";
import {toPageName} from "@/lib/analytics/page-name";
import {
    CashierOpenSource,
    ComponentName,
    TabName,
    trackButtonClicked,
    trackCashierOpened,
    trackPopupClosed,
    trackPopupOpened,
} from "@/lib/analytics/mixpanel-events";

/**
 * The single analytics subscriber on the dialog orchestrator's event bus
 * (AC-711, spec §9.1).
 *
 * Every dialog in the app is opened through the orchestrator, so `popup_opened`
 * and `popup_closed` — for all 18 dialogs, desktop and mobile — are covered here
 * with no edit to any dialog component. `cashier_opened` is derived from the same
 * stream, because it *is* a dialog open and every property it needs is in the
 * props bag at that instant: firing it from inside WalletDialog would duplicate
 * what this subscriber already sees.
 *
 * The auth `*_started` events are deliberately NOT here. They look like dialog
 * opens but are not: `registration_method` is unknown until AuthResolver resolves
 * the provider from the player's country, and the player can switch between login
 * and register without reopening. AuthResolver owns them — see its comment.
 *
 * Mounted once in the locale layout, inside <DialogOrchestratorProvider/>.
 */
export default function MixpanelDialogBridge() {
    useEffect(() => {
        const orchestrator = getOrchestrator();

        // `popup_closed` needs the popup's name, but by the time the engine emits
        // `closed` the instance is gone from the active set and its props are no
        // longer readable. Names are therefore captured at open time and consumed
        // on close. Keyed by instanceId, so two instances of the same dialog (e.g.
        // stacked CONFIRMs) cannot overwrite each other.
        const openPopups = new Map<string, PopupName>();

        const offOpened = orchestrator.on("opened", (event) => {
            if (!event.id || !event.instanceId) return;

            const props = orchestrator.getProps(event.instanceId);
            const popupName = resolvePopupName(event.id, props);
            openPopups.set(event.instanceId, popupName);

            trackPopupOpened({popupName, page: toPageName(event.ctx.route)});

            if (event.id === DialogsEnum.WALLET) {
                const openSource = toOpenSource(props?.openSource);

                trackCashierOpened({
                    defaultTab: toCashierTab(props?.tab),
                    openSource,
                });

                // `open_cashier` is derived here rather than from the five call
                // sites that open WALLET, for the same reason `cashier_opened` is:
                // the click's surface is already in the props bag. Every click
                // source is also a `ComponentName`, so the two properties cannot
                // drift apart into two vocabularies.
                //
                // A null source means a call site that never declared one — an
                // un-instrumented open — and `insufficient_funds` is the game
                // page opening the cashier on its own after a rejected bet
                // (AC-835). Neither is a click, so neither is reported as one.
                const clickSource = toClickSource(openSource);
                if (clickSource) {
                    trackButtonClicked({
                        buttonName: "open_cashier",
                        component: clickSource,
                        page: toPageName(event.ctx.route),
                    });
                }
            }
        });

        const offClosed = orchestrator.on("closed", (event) => {
            if (!event.instanceId) return;

            const popupName = openPopups.get(event.instanceId);
            // A request that was cancelled while still queued never opened, so it
            // has no `popup_opened` to pair with and must not emit a close.
            if (!popupName) return;
            openPopups.delete(event.instanceId);

            const closeType = resolveCloseType(event.result);
            trackPopupClosed({popupName, closeType});

            // `close_popup` is the X button, and `close_type` already identifies
            // it — so this is deliberately redundant with `popup_closed`. It is
            // derived here anyway because the alternative is an onClick on the
            // close affordance of all 18 dialogs × desktop/mobile, and because
            // `button_clicked` is where the spec puts every click. It inherits the
            // heuristic's error rate (see `close-intent.ts`); if the event volume
            // ever costs more than the convenience, this block is the one to drop.
            if (closeType === "x_button") {
                trackButtonClicked({
                    buttonName: "close_popup",
                    component: "popup",
                    page: toPageName(event.ctx.route),
                });
            }
        });

        const uninstallCloseIntent = installCloseIntentTracker();

        return () => {
            offOpened();
            offClosed();
            uninstallCloseIntent();
        };
    }, []);

    return null;
}

/**
 * The cashier's opening tab. `props.tab` is the untyped bag from
 * `orchestrator.open(WALLET, {props})`, so it is validated rather than cast;
 * WalletDialog itself defaults to the deposit tab, and this mirrors that.
 */
const toCashierTab = (tab: unknown): TabName => {
    switch (tab) {
        case WalletTabsEnum.WITHDRAW:
            return "withdraw";
        case WalletTabsEnum.HISTORY:
            return "history";
        default:
            return "deposit";
    }
};

/** The sources that are a click on a surface — each one is also a `ComponentName`. */
const CLICK_SOURCES: readonly (CashierOpenSource & ComponentName)[] = [
    "header",
    "mobile_menu",
    "bonus_card",
    "profile",
    "game",
    "popup",
];

const OPEN_SOURCES: readonly CashierOpenSource[] = [
    ...CLICK_SOURCES,
    "insufficient_funds",
];

/**
 * The `openSource` every WALLET call site passes in its props bag. It is
 * validated rather than cast — the bag is untyped, and an unrecognised value
 * must land as `null` (an un-instrumented call site) rather than as a junk value
 * that Mixpanel keeps in the Lexicon forever.
 */
const toOpenSource = (source: unknown): CashierOpenSource | null =>
    OPEN_SOURCES.find((known) => known === source) ?? null;

/** Narrows a source to the click surfaces `button_clicked` may report. */
const toClickSource = (source: CashierOpenSource | null): (CashierOpenSource & ComponentName) | null =>
    CLICK_SOURCES.find((known) => known === source) ?? null;
